import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useCRM } from '../utils/CRMContext';
import { useActivities } from '../hooks/useActivities';
import { useAuth } from '../utils/AuthContext';
import { Activity, ActivityType, GoogleCalendarEvent, CallOutcome } from '../types';
import { getProjectDisplayName } from '../utils/projectName';
import { 
  getOutcomeLabel,
  createFollowUpCallData,
  requiresFollowUp,
  calculateCallMetricsFromActivities,
  getCallCadenceConfig,
} from '../utils/callTracking';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  addMonths, 
  subMonths,
  addWeeks,
  subWeeks,
  isToday,
  setHours
} from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { 
  getUserGoogleCalendarConfig,
  fetchCalendarEvents,
  isGoogleCalendarConnected,
  createGoogleEvent,
  getDefaultCalendarForEvents,
} from '../utils/googleCalendarService';
import {
  Phone,
  PhoneOff,
  PhoneMissed,
  Voicemail,
  MessageSquare,
  Mail,
  Calendar,
  FileText,
  Home,
  User,
  Briefcase,
  Menu,
  CalendarDays,
  MapPin,
  X,
  type LucideIcon,
} from 'lucide-react';
import './CalendarPage.css';

// Call outcome options
const CALL_OUTCOMES: { value: CallOutcome; label: string; icon: LucideIcon; color: string }[] = [
  { value: 'answered', label: 'Answered', icon: Phone, color: '#22c55e' },
  { value: 'no_answer', label: 'No Answer', icon: PhoneMissed, color: '#ef4444' },
  { value: 'voicemail', label: 'Left Voicemail', icon: Voicemail, color: '#f59e0b' },
  { value: 'busy', label: 'Busy', icon: PhoneOff, color: '#6b7280' },
  { value: 'wrong_number', label: 'Wrong Number', icon: X, color: '#dc2626' },
  { value: 'disconnected', label: 'Disconnected', icon: PhoneOff, color: '#374151' },
];

const ACTIVITY_ICONS: Record<ActivityType, LucideIcon> = {
  call: Phone,
  text: MessageSquare,
  email: Mail,
  meeting: Calendar,
  note: FileText,
  'site-visit': Home,
};

const ACTIVITY_CONFIG: Record<ActivityType, { label: string; color: string }> = {
  call: { label: 'Call', color: '#4299E1' },
  text: { label: 'Text', color: '#48BB78' },
  email: { label: 'Email', color: '#ED8936' },
  meeting: { label: 'Meeting', color: '#9F7AEA' },
  note: { label: 'Note', color: '#A0AEC0' },
  'site-visit': { label: 'Site Visit', color: '#F56565' },
};

const ACTIVITY_TYPES: ActivityType[] = ['call', 'text', 'email', 'meeting', 'note', 'site-visit'];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 8 AM to 8 PM

type ViewMode = 'month' | 'week';
type FilterMode = 'all' | 'lead' | 'deal';

const CalendarPage: React.FC = () => {
  const { activities } = useActivities();
  const { leads, enhancedDeals, createActivity, completeActivity, deleteActivity, updateLead } = useCRM();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  
  // Quick add activity state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddType, setQuickAddType] = useState<ActivityType>('call');
  const [quickAddTime, setQuickAddTime] = useState('09:00');
  const [quickAddNote, setQuickAddNote] = useState('');
  const [quickAddEntity, setQuickAddEntity] = useState<{ type: 'lead' | 'deal'; id: string; name: string } | null>(null);
  
  // Call outcome modal state
  const [showCallOutcome, setShowCallOutcome] = useState(false);
  const [completingCallId, setCompletingCallId] = useState<string | null>(null);
  const [callNote, setCallNote] = useState('');
  const [isCompletingCall, setIsCompletingCall] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickAddSyncToGoogle, setQuickAddSyncToGoogle] = useState(false);
  
  // Google Calendar state
  const [googleCalendarEvents, setGoogleCalendarEvents] = useState<GoogleCalendarEvent[]>([]);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [showGoogleEvents, setShowGoogleEvents] = useState(true);
  const [loadingGoogleEvents, setLoadingGoogleEvents] = useState(false);
  
  // Check Google Calendar connection and fetch events
  useEffect(() => {
    const checkConnection = async () => {
      if (!user?.id) return;
      const connected = await isGoogleCalendarConnected(user.id);
      setGoogleCalendarConnected(connected);
    };
    checkConnection();
  }, [user?.id]);
  
  // Fetch Google Calendar events when view changes
  const fetchGoogleEvents = useCallback(async () => {
    if (!user?.id || !googleCalendarConnected) return;
    
    setLoadingGoogleEvents(true);
    try {
      const config = await getUserGoogleCalendarConfig(user.id);
      if (!config?.connected || !config.selectedCalendars?.length) return;
      
      // Calculate date range based on view mode
      let start: Date, end: Date;
      if (viewMode === 'month') {
        start = startOfWeek(startOfMonth(currentDate));
        end = endOfWeek(endOfMonth(currentDate));
      } else {
        start = startOfWeek(currentDate);
        end = endOfWeek(currentDate);
      }
      
      const events = await fetchCalendarEvents(
        user.id,
        config.selectedCalendars,
        start,
        end
      );
      setGoogleCalendarEvents(events);
    } catch (error) {
      console.error('Error fetching Google Calendar events:', error);
    } finally {
      setLoadingGoogleEvents(false);
    }
  }, [user?.id, googleCalendarConnected, viewMode, currentDate]);
  
  useEffect(() => {
    fetchGoogleEvents();
  }, [fetchGoogleEvents]);

  // Filter activities based on filter mode
  const filteredActivities = useMemo(() => {
    return activities.filter(activity => {
      if (filterMode === 'all') return true;
      return activity.entityType === filterMode;
    });
  }, [activities, filterMode]);

  // Get activities for a specific day
  const getActivitiesForDay = (day: Date): Activity[] => {
    return filteredActivities.filter(activity => {
      const activityDate = activity.scheduledAt || activity.dueDate || activity.completedAt;
      if (!activityDate) return false;
      return isSameDay(new Date(activityDate), day);
    }).sort((a, b) => {
      const dateA = a.scheduledAt || a.dueDate || a.completedAt;
      const dateB = b.scheduledAt || b.dueDate || b.completedAt;
      if (!dateA || !dateB) return 0;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
  };

  // Get Google Calendar events for a specific day
  const getGoogleEventsForDay = (day: Date): GoogleCalendarEvent[] => {
    if (!showGoogleEvents) return [];
    return googleCalendarEvents.filter(event => {
      return isSameDay(new Date(event.start), day);
    }).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  };

  // Get unique activity types for a day (for dots display)
  const getActivityTypesForDay = (day: Date): (ActivityType | 'google')[] => {
    const dayActivities = getActivitiesForDay(day);
    const types = new Set(dayActivities.map(a => a.type));
    const googleEvents = getGoogleEventsForDay(day);
    if (googleEvents.length > 0) {
      types.add('google' as any);
    }
    return Array.from(types) as (ActivityType | 'google')[];
  };

  // Generate calendar days for month view
  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate));
    const end = endOfWeek(endOfMonth(currentDate));
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  // Generate week days for week view
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate);
    const end = endOfWeek(currentDate);
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  // Navigation
  const goToPrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const goToNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Get activities for selected day
  const selectedDayActivities = useMemo(() => {
    return filteredActivities.filter(activity => {
      const activityDate = activity.scheduledAt || activity.dueDate || activity.completedAt;
      if (!activityDate) return false;
      return isSameDay(new Date(activityDate), selectedDate);
    }).sort((a, b) => {
      const dateA = a.scheduledAt || a.dueDate || a.completedAt;
      const dateB = b.scheduledAt || b.dueDate || b.completedAt;
      if (!dateA || !dateB) return 0;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
  }, [selectedDate, filteredActivities]);
  
  // Get Google Calendar events for selected day
  const selectedDayGoogleEvents = useMemo(() => {
    if (!showGoogleEvents) return [];
    return googleCalendarEvents.filter(event => 
      isSameDay(new Date(event.start), selectedDate)
    ).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  }, [selectedDate, googleCalendarEvents, showGoogleEvents]);

  // Group activities by time for week view
  const getActivitiesForHour = (day: Date, hour: number): Activity[] => {
    return getActivitiesForDay(day).filter(activity => {
      const activityDate = activity.scheduledAt || activity.dueDate;
      if (!activityDate) return false;
      return new Date(activityDate).getHours() === hour;
    });
  };
  
  // Get Google events for a specific hour
  const getGoogleEventsForHour = (day: Date, hour: number): GoogleCalendarEvent[] => {
    if (!showGoogleEvents) return [];
    return getGoogleEventsForDay(day).filter(event => {
      if (event.isAllDay) return hour === 8; // Show all-day events at 8 AM
      return new Date(event.start).getHours() === hour;
    });
  };

  // Complete activity handler
  const handleCompleteActivity = async (activity: Activity) => {
    if (!activity.id) return;
    
    // For call activities, show the outcome modal
    if (activity.type === 'call') {
      setCompletingCallId(activity.id);
      setShowCallOutcome(true);
      return;
    }
    
    try {
      await completeActivity(activity.id);
    } catch (err) {
      console.error('Error completing activity:', err);
    }
  };

  // Handle call outcome selection
  const handleCallOutcome = async (outcome: CallOutcome) => {
    if (!completingCallId) return;
    
    setIsCompletingCall(true);
    try {
      // Find the activity being completed
      const activity = activities.find(a => a.id === completingCallId);
      
      await completeActivity(completingCallId, {
        outcome: outcome,
        description: callNote || undefined,
      });
      
      // For lead activities, handle follow-up scheduling
      if (activity?.entityType === 'lead' && activity.entityId && requiresFollowUp(outcome)) {
        // Find the lead to get creation date
        const lead = leads.find(l => l.id === activity.entityId);
        const leadCreatedAt = lead?.createdAt || activity.createdAt;
        
        // Get all call activities for this lead to calculate metrics
        const leadCallActivities = activities.filter(
          a => a.entityType === 'lead' && 
               a.entityId === activity.entityId && 
               a.type === 'call' &&
               a.status === 'completed'
        );
        
        // Add this call to the list for accurate metrics
        const updatedActivities = [...leadCallActivities, {
          ...activity,
          status: 'completed' as const,
          completedAt: new Date(),
          outcome: outcome,
        }];
        
        const metrics = calculateCallMetricsFromActivities(
          updatedActivities,
          new Date(leadCreatedAt)
        );
        
        const currentAttempt = metrics.callAttemptNumber - 1;
        const callCadence = getCallCadenceConfig();
        
        // Create follow-up if not at max attempts
        if (currentAttempt < callCadence.maxAttempts) {
          const followUpData = createFollowUpCallData(
            activity.entityId,
            activity.entityName || 'Lead',
            user?.id || activity.createdBy,
            currentAttempt,
            new Date(leadCreatedAt)
          );
          
          if (followUpData) {
            await createActivity(followUpData);
          }
        }
        
        // Check if lead should be marked as lost
        if (metrics.shouldMarkLost && lead?.id) {
          const shouldMarkLost = window.confirm(
            `This lead has had ${metrics.unansweredCalls} unanswered calls in the past week. ` +
            `Would you like to mark them as lost?`
          );
          if (shouldMarkLost) {
            await updateLead(lead.id, { status: 'lost' });
          }
        }
      }
      
      setShowCallOutcome(false);
      setCompletingCallId(null);
      setCallNote('');
    } catch (error) {
      console.error('Error completing call:', error);
    } finally {
      setIsCompletingCall(false);
    }
  };

  // Delete activity handler
  const handleDeleteActivity = async (activity: Activity) => {
    if (!activity.id || !window.confirm('Delete this activity?')) return;
    try {
      await deleteActivity(activity.id);
    } catch (err) {
      console.error('Error deleting activity:', err);
    }
  };

  // Navigate to entity
  const handleEntityClick = (entityType: string, entityId: string) => {
    if (entityType === 'lead') {
      navigate('/', { state: { selectedLeadId: entityId } });
    } else if (entityType === 'deal') {
      navigate('/pipeline', { state: { selectedDealId: entityId } });
    }
  };

  // Quick add activity
  const handleQuickAdd = async () => {
    if (!user || !quickAddEntity) return;
    
    setIsSubmitting(true);
    try {
      const [hours, minutes] = quickAddTime.split(':').map(Number);
      const scheduledAt = new Date(selectedDate);
      scheduledAt.setHours(hours, minutes, 0, 0);

      const activityTitle = quickAddNote || `${ACTIVITY_CONFIG[quickAddType].label} - ${quickAddEntity.name}`;
      
      const activityData: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'> = {
        type: quickAddType,
        title: activityTitle,
        entityType: quickAddEntity.type,
        entityId: quickAddEntity.id,
        entityName: quickAddEntity.name,
        scheduledAt,
        createdBy: user.id,
        assignedTo: user.id,
        status: 'scheduled',
        syncToGoogle: quickAddSyncToGoogle && googleCalendarConnected,
      };

      // If syncing to Google Calendar, create the Google event first
      if (quickAddSyncToGoogle && googleCalendarConnected) {
        try {
          const calendarId = await getDefaultCalendarForEvents(user.id);
          const googleEventId = await createGoogleEvent(user.id, calendarId, activityData as Activity);
          activityData.googleEventId = googleEventId;
          activityData.googleCalendarId = calendarId;
        } catch (googleErr) {
          console.error('Error creating Google Calendar event:', googleErr);
          // Continue with activity creation even if Google sync fails
        }
      }

      await createActivity(activityData);

      setShowQuickAdd(false);
      setQuickAddNote('');
      setQuickAddEntity(null);
      setQuickAddSyncToGoogle(false);
    } catch (err) {
      console.error('Error creating activity:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get entity options for quick add
  const entityOptions = useMemo(() => {
    const options: { type: 'lead' | 'deal'; id: string; name: string }[] = [];
    
    leads.forEach(lead => {
      if (lead.id) {
        options.push({
          type: 'lead',
          id: lead.id,
          name: `${lead.firstName} ${lead.lastName}`.trim() || 'Unnamed Lead',
        });
      }
    });
    
    enhancedDeals.forEach(deal => {
      if (deal.id) {
        options.push({
          type: 'deal',
          id: deal.id,
          name: getProjectDisplayName(deal) || 'Unnamed Deal',
        });
      }
    });
    
    return options;
  }, [leads, enhancedDeals]);

  // Format time
  const formatActivityTime = (date: Date | undefined): string => {
    if (!date) return '';
    return format(new Date(date), 'h:mm a');
  };

  // Get events for a specific day (for month view display)
  const getEventsForDay = (day: Date) => {
    const dayActivities = getActivitiesForDay(day);
    const dayGoogleEvents = showGoogleEvents ? getGoogleEventsForDay(day) : [];
    
    const events: Array<{
      id: string;
      time: string;
      title: string;
      type: string;
      isGoogle?: boolean;
    }> = [];
    
    dayActivities.forEach(activity => {
      const activityDate = activity.scheduledAt || activity.dueDate;
      events.push({
        id: activity.id || '',
        time: activityDate ? format(new Date(activityDate), 'h:mma').toLowerCase() : '',
        title: activity.title,
        type: activity.type,
      });
    });
    
    dayGoogleEvents.forEach(event => {
      events.push({
        id: event.id,
        time: event.isAllDay ? '' : format(new Date(event.start), 'h:mma').toLowerCase(),
        title: event.title,
        type: 'google',
        isGoogle: true,
      });
    });
    
    return events.sort((a, b) => {
      if (!a.time) return -1;
      if (!b.time) return 1;
      return a.time.localeCompare(b.time);
    });
  };

  return (
    <div className="calendar-page">
      {/* Google Calendar Style Header */}
      <header className="calendar-header">
        <div className="calendar-header-left">
          <button className="menu-btn" onClick={() => navigate('/')}><Menu size={20} /></button>
          <div className="calendar-logo">
            <span className="calendar-logo-icon"><CalendarDays size={20} /></span>
            <span className="calendar-logo-text">Calendar</span>
          </div>
        </div>
        
        <div className="calendar-header-center">
          <button className="today-btn" onClick={goToToday}>Today</button>
          <div className="nav-arrows">
            <button className="nav-btn" onClick={goToPrevious}>‹</button>
            <button className="nav-btn" onClick={goToNext}>›</button>
          </div>
          <span className="current-date">
            {viewMode === 'month' 
              ? format(currentDate, 'MMMM yyyy')
              : format(currentDate, 'MMMM yyyy')
            }
          </span>
        </div>
        
        <div className="calendar-header-right">
          {googleCalendarConnected && (
            <label className="google-calendar-toggle">
              <input
                type="checkbox"
                checked={showGoogleEvents}
                onChange={(e) => setShowGoogleEvents(e.target.checked)}
              />
              <span className="google-icon"><Calendar size={16} /></span>
              Google
              {loadingGoogleEvents && <span className="loading-indicator">...</span>}
            </label>
          )}
          <select 
            className="filter-select"
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value as FilterMode)}
          >
            <option value="all">All</option>
            <option value="lead">Leads</option>
            <option value="deal">Deals</option>
          </select>
          <div className="view-toggle">
            <button 
              className={viewMode === 'month' ? 'active' : ''}
              onClick={() => setViewMode('month')}
            >
              Month
            </button>
            <button 
              className={viewMode === 'week' ? 'active' : ''}
              onClick={() => setViewMode('week')}
            >
              Week
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="calendar-content">
        {/* Left Sidebar with Mini Calendar */}
        <aside className="calendar-sidebar">
          <div className="mini-calendar">
            <div className="mini-calendar-header">
              <h3>{format(currentDate, 'MMMM yyyy')}</h3>
              <div className="mini-calendar-nav">
                <button className="mini-nav-btn" onClick={goToPrevious}>‹</button>
                <button className="mini-nav-btn" onClick={goToNext}>›</button>
              </div>
            </div>
            <div className="mini-calendar-grid">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="mini-day-header">{day}</div>
              ))}
              {calendarDays.slice(0, 42).map(day => (
                <div
                  key={day.toISOString()}
                  className={`mini-day ${!isSameMonth(day, currentDate) ? 'other-month' : ''} ${isToday(day) ? 'today' : ''} ${isSameDay(day, selectedDate) ? 'selected' : ''}`}
                  onClick={() => setSelectedDate(day)}
                >
                  {format(day, 'd')}
                </div>
              ))}
            </div>
          </div>
          
          <div className="my-calendars">
            <h4>My Calendars</h4>
            <div className="calendar-list">
              <div className="calendar-item">
                <span className="calendar-color" style={{ background: '#4299E1' }}></span>
                <span className="calendar-name">Calls</span>
              </div>
              <div className="calendar-item">
                <span className="calendar-color" style={{ background: '#9F7AEA' }}></span>
                <span className="calendar-name">Meetings</span>
              </div>
              <div className="calendar-item">
                <span className="calendar-color" style={{ background: '#F56565' }}></span>
                <span className="calendar-name">Site Visits</span>
              </div>
              {googleCalendarConnected && (
                <div className="calendar-item">
                  <span className="calendar-color" style={{ background: '#1a73e8' }}></span>
                  <span className="calendar-name">Google Calendar</span>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Calendar Grid */}
        <div className="calendar-grid-container">
          {viewMode === 'month' ? (
            <div className="month-view">
              {/* Day Headers */}
              <div className="day-headers">
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                  <div key={day} className="day-header">{day}</div>
                ))}
              </div>
              
              {/* Calendar Days */}
              <div className="month-grid">
                {calendarDays.map(day => {
                  const dayEvents = getEventsForDay(day);
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const isSelected = isSameDay(day, selectedDate);
                  
                  return (
                    <div 
                      key={day.toISOString()}
                      className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday(day) ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedDate(day)}
                    >
                      <span className="day-number">{format(day, 'd')}</span>
                      <div className="day-events">
                        {dayEvents.slice(0, 3).map((event) => (
                          <div 
                            key={event.id} 
                            className={`event-item ${event.isGoogle ? 'google-event' : event.type}`}
                            title={event.title}
                          >
                            {event.time && <span className="event-time">{event.time}</span>}
                            <span className="event-title">{event.title}</span>
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="more-events">
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="week-view">
              {/* Time Column + Day Headers */}
              <div className="week-header">
                <div className="time-column-header"></div>
                {weekDays.map(day => (
                  <div 
                    key={day.toISOString()} 
                    className={`week-day-header ${isToday(day) ? 'today' : ''} ${isSameDay(day, selectedDate) ? 'selected' : ''}`}
                    onClick={() => setSelectedDate(day)}
                  >
                    <span className="week-day-name">{format(day, 'EEE')}</span>
                    <span className="week-day-number">{format(day, 'd')}</span>
                  </div>
                ))}
              </div>
              
              {/* Time Slots */}
              <div className="week-body">
                {HOURS.map(hour => (
                  <div key={hour} className="time-row">
                    <div className="time-label">
                      {format(setHours(new Date(), hour), 'h a')}
                    </div>
                    {weekDays.map(day => {
                      const hourActivities = getActivitiesForHour(day, hour);
                      const hourGoogleEvents = getGoogleEventsForHour(day, hour);
                      return (
                        <div 
                          key={day.toISOString()} 
                          className={`time-slot ${isToday(day) ? 'today' : ''}`}
                          onClick={() => setSelectedDate(day)}
                        >
                          <div className="time-slot-events">
                            {hourActivities.map(activity => {
                              const IconComponent = ACTIVITY_ICONS[activity.type];
                              return (
                                <div 
                                  key={activity.id}
                                  className={`time-slot-activity ${activity.type}`}
                                  title={activity.title}
                                >
                                  {IconComponent && <IconComponent size={12} style={{ marginRight: 4 }} />}{activity.title}
                                </div>
                              );
                            })}
                            {hourGoogleEvents.map(event => (
                              <div 
                                key={event.id}
                                className="time-slot-activity google-event"
                                title={event.title}
                              >
                                <Calendar size={12} style={{ marginRight: 4 }} />{event.title}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - Activities */}
        <aside className="activities-sidebar">
          <div className="sidebar-header">
            <h2>{isToday(selectedDate) ? 'Today' : format(selectedDate, 'EEEE')}</h2>
            <p>{format(selectedDate, 'MMMM d, yyyy')}</p>
          </div>

          <div className="sidebar-content">
            {/* Activities List */}
            <div className="activities-list">
              {selectedDayActivities.length === 0 && selectedDayGoogleEvents.length === 0 ? (
                <div className="no-activities">
                  <p>No events scheduled</p>
                  <button 
                    className="quick-add-btn primary"
                    onClick={() => setShowQuickAdd(true)}
                  >
                    + Create
                  </button>
                </div>
              ) : (
                <>
                  {/* CRM Activities */}
                  {selectedDayActivities.map(activity => {
                    const IconComponent = ACTIVITY_ICONS[activity.type];
                    return (
                    <div key={activity.id} className="activity-card">
                      <div className="activity-card-header">
                        <div 
                          className="activity-icon"
                          style={{ background: `${ACTIVITY_CONFIG[activity.type]?.color}20` }}
                        >
                          {IconComponent && <IconComponent size={16} />}
                        </div>
                        <div className="activity-card-content">
                          <div className="activity-title">{activity.title}</div>
                          <div className="activity-meta">
                            <span className={`activity-type-indicator ${activity.type}`}></span>
                            {formatActivityTime(activity.scheduledAt || activity.dueDate)}
                            {activity.status === 'completed' && ' • Completed'}
                          </div>
                        </div>
                      </div>
                      {activity.entityName && (
                        <div className="activity-entity">
                          <span 
                            className="entity-link"
                            onClick={() => handleEntityClick(activity.entityType, activity.entityId)}
                          >
                            {activity.entityType === 'lead' ? <User size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <Briefcase size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}{activity.entityName}
                          </span>
                        </div>
                      )}
                      <div className="activity-actions">
                        {activity.status !== 'completed' && (
                          <button 
                            className="activity-btn complete"
                            onClick={() => handleCompleteActivity(activity)}
                          >
                            ✓ Complete
                          </button>
                        )}
                        <button 
                          className="activity-btn delete"
                          onClick={() => handleDeleteActivity(activity)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                  })}
                  
                  {/* Google Calendar Events */}
                  {selectedDayGoogleEvents.map(event => (
                    <div key={event.id} className="activity-card google-event">
                      <div className="activity-card-header">
                        <div className="activity-icon" style={{ background: 'var(--color-info-light)' }}>
                          <Calendar size={16} />
                        </div>
                        <div className="activity-card-content">
                          <div className="activity-title">{event.title}</div>
                          <div className="activity-meta">
                            <span className="activity-type-indicator google"></span>
                            {event.isAllDay ? 'All day' : format(new Date(event.start), 'h:mm a')}
                            <span className="google-badge">Google</span>
                          </div>
                          {event.location && (
                            <div className="event-location"><MapPin size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{event.location}</div>
                          )}
                        </div>
                      </div>
                      {event.htmlLink && (
                        <div className="activity-actions">
                          <a 
                            href={event.htmlLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="open-in-google-btn"
                          >
                            Open in Google Calendar ↗
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Quick Add Form */}
          {showQuickAdd ? (
            <div className="quick-add-form">
              <h3>New Activity</h3>
              <div className="quick-add-row">
                <select
                  className="quick-add-type-select"
                  value={quickAddType}
                  onChange={(e) => setQuickAddType(e.target.value as ActivityType)}
                >
                  {ACTIVITY_TYPES.map(type => (
                    <option key={type} value={type}>
                      {ACTIVITY_CONFIG[type].label}
                    </option>
                  ))}
                </select>
                <input
                  type="time"
                  className="quick-add-time-input"
                  value={quickAddTime}
                  onChange={(e) => setQuickAddTime(e.target.value)}
                />
              </div>
              <select
                className="quick-add-entity-select"
                value={quickAddEntity ? `${quickAddEntity.type}-${quickAddEntity.id}` : ''}
                onChange={(e) => {
                  const [type, id] = e.target.value.split('-');
                  const option = entityOptions.find(o => o.type === type && o.id === id);
                  setQuickAddEntity(option || null);
                }}
              >
                <option value="">Select Lead or Deal...</option>
                <optgroup label="Leads">
                  {entityOptions.filter(o => o.type === 'lead').map(option => (
                    <option key={`${option.type}-${option.id}`} value={`${option.type}-${option.id}`}>
                      {option.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Deals">
                  {entityOptions.filter(o => o.type === 'deal').map(option => (
                    <option key={`${option.type}-${option.id}`} value={`${option.type}-${option.id}`}>
                      {option.name}
                    </option>
                  ))}
                </optgroup>
              </select>
              <input
                type="text"
                className="quick-add-note-input"
                placeholder="Add title or note..."
                value={quickAddNote}
                onChange={(e) => setQuickAddNote(e.target.value)}
              />
              {googleCalendarConnected && (
                <label className="google-calendar-sync-toggle">
                  <input
                    type="checkbox"
                    checked={quickAddSyncToGoogle}
                    onChange={(e) => setQuickAddSyncToGoogle(e.target.checked)}
                  />
                  Sync to Google Calendar
                </label>
              )}
              <div className="quick-add-actions">
                <button 
                  className="quick-add-btn secondary"
                  onClick={() => setShowQuickAdd(false)}
                >
                  Cancel
                </button>
                <button 
                  className="quick-add-btn primary"
                  onClick={handleQuickAdd}
                  disabled={!quickAddEntity || isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <div className="quick-add-form">
              <button 
                className="quick-add-btn primary"
                onClick={() => setShowQuickAdd(true)}
                style={{ width: '100%' }}
              >
                + Create Activity
              </button>
            </div>
          )}
        </aside>
      </div>

      {/* Call Outcome Modal */}
      {showCallOutcome && (
        <div className="call-outcome-overlay" onClick={() => { setShowCallOutcome(false); setCompletingCallId(null); }}>
          <div className="call-outcome-modal" onClick={e => e.stopPropagation()}>
            <div className="call-outcome-header">
              <Phone size={20} />
              <h4>Complete Call</h4>
              <button className="close-btn" onClick={() => { setShowCallOutcome(false); setCompletingCallId(null); }}>
                <X size={18} />
              </button>
            </div>
            
            <p className="call-outcome-subtitle">How did the call go?</p>
            
            <div className="call-outcome-options">
              {CALL_OUTCOMES.map(option => {
                const IconComponent = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className="call-outcome-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCallOutcome(option.value);
                    }}
                    disabled={isCompletingCall}
                    style={{ '--outcome-color': option.color } as React.CSSProperties}
                  >
                    <IconComponent size={20} />
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>

            <input
              type="text"
              className="call-note-input"
              placeholder="Add notes (optional)"
              value={callNote}
              onChange={e => setCallNote(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
